import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner, IconButton, Tooltip,
  Badge, Table, Thead, Tbody, Tr, Th, Td, Select,
  Stat, StatLabel, StatNumber, StatGroup,
  Alert, AlertIcon, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { FiRefreshCw, FiChevronLeft, FiChevronRight, FiRotateCcw } from 'react-icons/fi';

const API_BASE = '/api/v1';
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Token ${localStorage.getItem('token')}`,
});

const statusColor = (s) => {
  switch (s) {
    case 'completed': return 'green';
    case 'failed': return 'red';
    case 'running': return 'blue';
    case 'pending': return 'yellow';
    default: return 'gray';
  }
};

const fmtSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(2)} GB`;
};

/* ────────────────────────────────
   Overview Stats
   ──────────────────────────────── */
function OverviewStats({ overview }) {
  if (!overview) return null;
  return (
    <StatGroup mb={6}>
      <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
        <StatLabel>Total Backups (90d)</StatLabel>
        <StatNumber>{overview.total_backups}</StatNumber>
      </Stat>
      <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
        <StatLabel>Completed</StatLabel>
        <StatNumber color="green.500">{overview.completed}</StatNumber>
      </Stat>
      <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
        <StatLabel>Failed</StatLabel>
        <StatNumber color="red.500">{overview.failed}</StatNumber>
      </Stat>
      <Stat bg="white" p={4} borderRadius="lg" boxShadow="sm">
        <StatLabel>Running Now</StatLabel>
        <StatNumber color="blue.500">{overview.running?.length || 0}</StatNumber>
      </Stat>
    </StatGroup>
  );
}

/* ────────────────────────────────
   Active / Ongoing Backups
   ──────────────────────────────── */
function ActiveBackups({ overview }) {
  const active = [...(overview?.running || []), ...(overview?.pending || [])];
  if (active.length === 0) return null;
  return (
    <Box mb={6}>
      <Heading size="sm" mb={3}>Ongoing Backups</Heading>
      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="blue.50">
            <Tr>
              <Th>Target</Th><Th>Type</Th><Th>Status</Th><Th>Started</Th>
            </Tr>
          </Thead>
          <Tbody>
            {active.map(r => (
              <Tr key={r.id}>
                <Td fontWeight="medium">{r.target_name}</Td>
                <Td><Badge colorScheme={r.target_type === 'postgres' ? 'blue' : 'purple'}>{r.target_type}</Badge></Td>
                <Td><Badge colorScheme={statusColor(r.status)}>{r.status}</Badge></Td>
                <Td fontSize="xs">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

/* ────────────────────────────────
   Upcoming Schedules
   ──────────────────────────────── */
function UpcomingSchedules({ overview }) {
  const scheds = overview?.upcoming_schedules || [];
  if (scheds.length === 0) return null;
  return (
    <Box mb={6}>
      <Heading size="sm" mb={3}>Upcoming Scheduled Backups</Heading>
      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="green.50">
            <Tr>
              <Th>Target</Th><Th>Type</Th><Th>Interval</Th><Th>Next Run</Th>
            </Tr>
          </Thead>
          <Tbody>
            {scheds.map(s => (
              <Tr key={s.id}>
                <Td fontWeight="medium">{s.target_name}</Td>
                <Td><Badge colorScheme={s.target_type === 'postgres' ? 'blue' : 'purple'}>{s.target_type}</Badge></Td>
                <Td>{s.interval}</Td>
                <Td fontSize="xs">{new Date(s.next_run).toLocaleString()}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

/* ────────────────────────────────
   Backup History (paginated)
   ──────────────────────────────── */
function BackupHistoryTable() {
  const [records, setRecords] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const { isOpen: isRestoreOpen, onOpen: onRestoreOpen, onClose: onRestoreClose } = useDisclosure();
  const toast = useToast();

  const fetchRecords = useCallback(() => {
    setLoading(true);
    let url = `${API_BASE}/backup-records/?page=${page}&page_size=${pageSize}`;
    if (statusFilter) url += `&status=${statusFilter}`;
    fetch(url, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => {
        setRecords(d.results || []);
        setCount(d.count || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page, pageSize, statusFilter]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const totalPages = Math.ceil(count / pageSize);

  const openRestoreConfirm = (record) => {
    setSelectedRecord(record);
    onRestoreOpen();
  };

  const handleRestore = async () => {
    if (!selectedRecord) return;
    onRestoreClose();
    try {
      const res = await fetch(`${API_BASE}/backup-records/${selectedRecord.id}/restore/`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast({
        title: 'Restore started',
        description: `Restore #${data.id} is running in the background. The target database will be overwritten.`,
        status: 'warning',
        duration: 8000,
        isClosable: true,
      });
    } catch (err) {
      toast({ title: 'Restore failed to start', description: err.message, status: 'error', duration: 5000 });
    }
    setSelectedRecord(null);
  };

  return (
    <Box>
      <HStack justify="space-between" mb={3}>
        <Heading size="sm">Backup History (last 90 days)</Heading>
        <HStack>
          <Select size="xs" w="130px" value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All statuses</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="running">Running</option>
            <option value="pending">Pending</option>
          </Select>
          <Button size="xs" leftIcon={<FiRefreshCw />} onClick={fetchRecords}>Refresh</Button>
        </HStack>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        {loading ? (
          <Box textAlign="center" py={10}><Spinner /></Box>
        ) : (
          <>
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>ID</Th><Th>Target</Th><Th>Type</Th><Th>Status</Th>
                  <Th>Started</Th><Th>Completed</Th><Th>Size</Th><Th>S3 Key</Th><Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {records.map(r => (
                  <Tr key={r.id}>
                    <Td>{r.id}</Td>
                    <Td fontWeight="medium">{r.target_name}</Td>
                    <Td><Badge colorScheme={r.target_type === 'postgres' ? 'blue' : 'purple'}>{r.target_type}</Badge></Td>
                    <Td><Badge colorScheme={statusColor(r.status)}>{r.status}</Badge></Td>
                    <Td fontSize="xs">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</Td>
                    <Td fontSize="xs">{r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'}</Td>
                    <Td fontSize="xs">{fmtSize(r.size_bytes)}</Td>
                    <Td fontSize="xs" maxW="250px" isTruncated title={r.s3_key}>{r.s3_key || '—'}</Td>
                    <Td>
                      {r.status === 'completed' && r.s3_key && (
                        <Tooltip label="Restore this backup">
                          <IconButton
                            size="xs"
                            icon={<FiRotateCcw />}
                            colorScheme="orange"
                            variant="ghost"
                            onClick={() => openRestoreConfirm(r)}
                            aria-label="Restore"
                          />
                        </Tooltip>
                      )}
                    </Td>
                  </Tr>
                ))}
                {records.length === 0 && (
                  <Tr><Td colSpan={9} textAlign="center" py={6} color="gray.400">No backup records found</Td></Tr>
                )}
              </Tbody>
            </Table>

            {/* Pagination controls */}
            <HStack justify="space-between" px={4} py={3} bg="gray.50" borderTop="1px" borderColor="gray.100">
              <Text fontSize="xs" color="gray.500">{count} record(s) — page {page} of {totalPages || 1}</Text>
              <HStack>
                <Button size="xs" leftIcon={<FiChevronLeft />} isDisabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                <Button size="xs" rightIcon={<FiChevronRight />} isDisabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </HStack>
            </HStack>
          </>
        )}
      </Box>

      {/* Restore confirmation modal */}
      <Modal isOpen={isRestoreOpen} onClose={onRestoreClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Restore</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" borderRadius="md" mb={4}>
              <AlertIcon />
              This will <strong>overwrite the current database</strong> and cannot be undone.
            </Alert>
            {selectedRecord && (
              <Text fontSize="sm">
                Restore <strong>{selectedRecord.target_name}</strong> from backup taken{' '}
                <strong>{new Date(selectedRecord.created_at).toLocaleString()}</strong>{' '}
                ({fmtSize(selectedRecord.size_bytes)})?
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRestoreClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={handleRestore}>Restore</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

/* ────────────────────────────────
   Restore History
   ──────────────────────────────── */
function RestoreHistoryTable() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRestores = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/restore-records/`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => { setRecords(d.results || d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchRestores(); }, [fetchRestores]);

  if (!loading && records.length === 0) return null;

  return (
    <Box mt={8}>
      <HStack justify="space-between" mb={3}>
        <Heading size="sm">Restore History</Heading>
        <Button size="xs" leftIcon={<FiRefreshCw />} onClick={fetchRestores}>Refresh</Button>
      </HStack>
      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        {loading ? (
          <Box textAlign="center" py={6}><Spinner /></Box>
        ) : (
          <Table variant="simple" size="sm">
            <Thead bg="orange.50">
              <Tr>
                <Th>ID</Th><Th>Target</Th><Th>Status</Th>
                <Th>Started</Th><Th>Completed</Th><Th>Error</Th>
              </Tr>
            </Thead>
            <Tbody>
              {records.map(r => (
                <Tr key={r.id}>
                  <Td>{r.id}</Td>
                  <Td fontWeight="medium">{r.target_name}</Td>
                  <Td><Badge colorScheme={statusColor(r.status)}>{r.status}</Badge></Td>
                  <Td fontSize="xs">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</Td>
                  <Td fontSize="xs">{r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'}</Td>
                  <Td fontSize="xs" maxW="300px" isTruncated title={r.error_message}>{r.error_message || '—'}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </Box>
  );
}

/* ────────────────────────────────
   Main Page
   ──────────────────────────────── */
export default function BackupHistory() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/backups/overview/`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => { setOverview(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Backups</Heading>
          <Text color="gray.500">History, ongoing backups, and upcoming schedules</Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="gray" size="sm" onClick={fetchOverview}>
          Refresh
        </Button>
      </HStack>

      {loading ? (
        <Box textAlign="center" py={20}><Spinner size="xl" /></Box>
      ) : (
        <>
          <OverviewStats overview={overview} />
          <ActiveBackups overview={overview} />
          <UpcomingSchedules overview={overview} />
          <BackupHistoryTable />
          <RestoreHistoryTable />
        </>
      )}
    </Box>
  );
}
